'use client'

import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { ClockIcon } from '@/app/[locale]/_assets/icon-components'
import { Box, HStack, Text, useDisclosure, VStack } from '@chakra-ui/react'
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogRoot,
} from '@/components/ui/dialog'
import { Avatar } from '@/components/ui/avatar'
import OffHoursModal from './OffHoursModal'
import { Form } from './form'
import { IWorkHour } from '@/app/[locale]/_types'

interface IProps {
  scheduleData?: { working_hours: IWorkHour[] }
  phone?: string
  formattedTime: string
  interval: number
  onConfirm: (time: string) => void
  showTrigger?: boolean
  open?: boolean
  onOpen?: () => void
  onClose?: () => void
}

export default function TimeDesktop({
  phone,
  formattedTime,
  interval = 0,
  scheduleData,
  onConfirm,
  showTrigger = true,
  open: controlledOpen,
  onOpen: controlledOnOpen,
  onClose: controlledOnClose,
}: IProps) {
  const t = useTranslations()
  const disclosure = useDisclosure()
  const open = controlledOpen ?? disclosure.open
  const onOpen = controlledOnOpen ?? disclosure.onOpen
  const onClose = controlledOnClose ?? disclosure.onClose

  return (
    <Box mt={6}>
      {showTrigger && (
        <>
          <Text fontSize='xl' fontWeight={600} mb={4} lineHeight={1.2}>
            {t('order_for_a_specific_time')}
          </Text>
          <HStack py={1}>
            <HStack gap={2} flex={1} h={6}>
              <Avatar
                bg={'background'}
                fallback={<ClockIcon color='var(--text-default)' />}
                rounded='2xl'
              />
              <VStack align='flex-start' justify='center' gap={1.5}>
                <Text
                  fontWeight='500'
                  color={formattedTime ? 'inherit' : 'gray.600'}
                  lineHeight={1}
                >
                  {formattedTime}
                </Text>
              </VStack>
            </HStack>
            <Button
              size='sm'
              variant='outline'
              bg={'background'}
              color={'text'}
              _hover={{
                bg: 'background.card',
              }}
              onClick={onOpen}
            >
              {t('change')}
            </Button>
          </HStack>
          <OffHoursModal onPreorder={onOpen} />
        </>
      )}

      <DialogRoot
        open={open}
        onOpenChange={onClose}
        scrollBehavior='inside'
        id='time-select'
      >
        <DialogBackdrop />
        <DialogContent maxW='600px'>
          <DialogBody pt={6} px={2} pb={0}>
            <Form
              phone={phone}
              interval={interval}
              scheduleData={scheduleData}
              onConfirm={(val) => {
                onClose()
                onConfirm(val)
              }}
            />
          </DialogBody>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}
